import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {useDispatch} from 'react-redux';

import {labels} from '../../../../../util/utils';
import {componentsTabId, groupRiskStatus} from '../../../../../util/uiconstants';
import Utility from '../../../../../util/utilityfunctions';

import {updateReconciliationRisk} from '../../../../../actions/Reconciliations';
import selectProjectAndEngIds from '../../../../../reducers/Reconciliations/selectors/selectProjectAndEngIds';
import selectUnmappedComponentRisks from '../../../../../reducers/Reconciliations/selectors/selectUnmappedComponentRisks';

import OutputText from '@ey/canvascoreservices/OutputText';

import TabNavigation from '../../../../CoreWrappers/TabNavigation';
import Badge from '../../../../CoreWrappers/Badge';
import Modal from '../../../../CoreWrappers/Modal';
import HrAlt from '../../../../Common/Hr/HrAlt';
import UnmappedRisksTable from './UnmappedRisksTable';
import StyledTrigger from './StyledTrigger';
import StyledUnmappedRisksModal from './StyledUnmappedRisksModal';

const UnmappedRisksModal = ({hasUnmmapedComponentRisks}) => {
	const notRelatedTab = {
		id: componentsTabId.NotRelated,
		label: labels.notRelatedTab,
		filterBy: ({groupRiskStatusId}) => groupRiskStatusId === groupRiskStatus.unmapped,
		instructional: labels.unmappedRisksModalInstructional,
		actionColumnHeaderTitletip: labels.notRelatedActionTitletip,
		actionColumnButtonLabel: labels.notApplicableTab,
		action: (riskId) => handleUpdateRisk(riskId, groupRiskStatus.notApplicable)
	};

	const notApplicableTab = {
		id: componentsTabId.NotApplicable,
		label: labels.notApplicableTab,
		filterBy: ({groupRiskStatusId}) => groupRiskStatusId === groupRiskStatus.notApplicable,
		instructional: labels.notApplicableRisksModalInstructional,
		actionColumnHeaderTitletip: labels.notApplicableActionTitletip,
		actionColumnButtonLabel: labels.undoNotApplicable,
		action: (riskId) => handleUpdateRisk(riskId, groupRiskStatus.unmapped)
	};

	const [show, setShow] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [activeTab, setActiveTab] = useState(notRelatedTab);

	const tabs = [notRelatedTab, notApplicableTab];

	const unmappedComponentRisks = selectUnmappedComponentRisks();
	const apiConfig = selectProjectAndEngIds();

	const dispatch = useDispatch();

	const onHide = () => setShow(false);

	const handleUpdateRisk = (riskId, statusId) => {
		setIsLoading(true);
		dispatch(updateReconciliationRisk(riskId, {groupRiskStatusId: statusId}, apiConfig)).finally(() =>
			setIsLoading(false)
		);
	};

	const renderTrigger = () => {
		const props = {
			size: 'small',
			variant: 'text-alt',
			label: labels.viewComponentTeamRisks,
			disabled: !hasUnmmapedComponentRisks,
			onClick: () => {
				setShow(true);
			}
		};

		return (
			<StyledTrigger {...props}>
				<Badge value={unmappedComponentRisks.length} />
			</StyledTrigger>
		);
	};

	const renderTabNavigation = () => {
		const props = {
			tabs,
			activeTab,
			setTab: (newTab) => setActiveTab(newTab),
			size: 'md'
		};

		return <TabNavigation {...props} />;
	};

	const renderTable = () => {
		const props = {
			data: unmappedComponentRisks.filter(activeTab.filterBy),
			tab: activeTab
		};

		return <UnmappedRisksTable {...props} />;
	};

	const renderModal = () => {
		const props = {
			show,
			modalsize: 'medium',
			title: labels.unmappedRisksModalTitle,
			labels: {
				modalCancelBtnLabel: labels.closeLabel,
				modalCloseBtnTitletip: labels.closeLabel,
				modalConfirmBtnLabel: labels.closeLabel
			},
			hideCancelButton: true,
			onOkClick: () => onHide(),
			onClose: () => onHide(),
			isOkDisabled: isLoading,
			disableClose: isLoading,
			isLoading
		};

		return (
			<Modal {...props}>
				<StyledUnmappedRisksModal>
					<OutputText light>{Utility.createRichTextElement(activeTab.instructional)}</OutputText>
					<HrAlt $color="--neutrals-100" />
					{renderTabNavigation()}
					{renderTable()}
				</StyledUnmappedRisksModal>
			</Modal>
		);
	};

	return (
		<>
			{renderTrigger()}
			{renderModal()}
		</>
	);
};

UnmappedRisksModal.propTypes = {};

export default UnmappedRisksModal;
